import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { useToast } from '../../context/ToastContext';
import DataTable from '../common/DataTable';
import Modal from '../common/Modal';
import InvoiceForm from '../common/InvoiceForm';
import PrintPreview from '../common/PrintPreview';
import { Plus, Edit2, Trash2, Printer } from 'lucide-react';
import { formatCurrency, formatDate } from '../../utils/helpers';

export default function PurchaseReturn() {
  const { state, dispatch } = useApp();
  const { addToast } = useToast();
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [printData, setPrintData] = useState(null);

  const handleSave = (data) => {
    if (editData) {
      dispatch({ type: 'DELETE_PURCHASE_RETURN', payload: editData.id });
      dispatch({ type: 'ADD_PURCHASE_RETURN', payload: data });
      addToast('Purchase return updated successfully', 'success');
    } else {
      dispatch({ type: 'ADD_PURCHASE_RETURN', payload: data });
      addToast('Purchase return created successfully', 'success');
    }
    closeForm();
  };

  const handleEdit = (row) => { setEditData(row); setShowForm(true); };
  const handleDelete = (row) => {
    if (window.confirm(`Delete return ${row.returnNo}? Stock and ledger will be reversed.`)) {
      dispatch({ type: 'DELETE_PURCHASE_RETURN', payload: row.id });
    }
  };
  const closeForm = () => { setShowForm(false); setEditData(null); };

  if (printData) return <PrintPreview type="purchaseReturn" data={printData} onClose={() => setPrintData(null)} />;

  const columns = [
    { key: 'returnNo', label: 'Return No', sortable: true },
    { key: 'date', label: 'Date', render: (v) => formatDate(v) },
    { key: 'partyId', label: 'Supplier', render: (v) => state.parties.find(p => p.id === v)?.name || '-' },
    { key: 'grandTotal', label: 'Amount', align: 'right', render: (v) => formatCurrency(v) },
  ];

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Purchase Returns (Debit Note)</h1>
        <button onClick={() => { setEditData(null); setShowForm(true); }} className="btn btn-primary flex items-center gap-2">
          <Plus size={16} /> New Return
        </button>
      </div>
      <div className="card">
        <DataTable
          columns={columns}
          data={[...state.purchaseReturns].reverse()}
          searchFields={['returnNo']}
          emptyState={{ title: 'No purchase returns yet', description: 'Create your first purchase return.', actionLabel: 'New Return', onAction: () => setShowForm(true) }}
          actions={(row) => (
            <>
              <button onClick={() => handleEdit(row)} className="p-1.5 rounded hover:bg-amber-50 text-amber-600" title="Edit"><Edit2 size={15} /></button>
              <button onClick={() => setPrintData(row)} className="p-1.5 rounded hover:bg-green-50 text-green-600" title="Print"><Printer size={15} /></button>
              <button onClick={() => handleDelete(row)} className="p-1.5 rounded hover:bg-red-50 text-red-600" title="Delete"><Trash2 size={15} /></button>
            </>
          )}
        />
      </div>
      <Modal isOpen={showForm} onClose={closeForm} title={editData ? `Edit Return ${editData.returnNo}` : 'New Purchase Return (Debit Note)'} size="xl">
        <InvoiceForm
          key={editData ? editData.id : 'new'}
          type="purchaseReturn"
          onSave={handleSave}
          onCancel={closeForm}
          initialData={editData || undefined}
          nextEntryNo={editData ? editData.returnNo : `PR-${String(state.counters.purchaseReturn + 1).padStart(4, '0')}`}
        />
      </Modal>
    </div>
  );
}
